import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Tabs,
  Tab,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { AddressAutoComplete, AddressAutoCompleteType } from '../../../../common/inputs/AddressAutoComplete';

const useStyles = makeStyles((theme: any) => ({
  tabPanel: {
    paddingTop: theme.spacing(2),
  },
  field: {
    marginBottom: theme.spacing(2),
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role='tabpanel' hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (addressJson: string) => void;
  initialValue?: string;
}

export function AddressInputDialog({ open, onClose, onSave, initialValue }: Props) {
  const classes = useStyles();
  const [tabIndex, setTabIndex] = React.useState(0);

  // Manual address fields
  const [lineOne, setLineOne] = React.useState('');
  const [lineTwo, setLineTwo] = React.useState('');
  const [suburb, setSuburb] = React.useState('');
  const [state, setState] = React.useState('');
  const [postcode, setPostcode] = React.useState('');
  const [country, setCountry] = React.useState('');

  // Places address fields
  const [selectedPlace, setSelectedPlace] = React.useState<AddressAutoCompleteType | undefined>(undefined);

  React.useEffect(() => {
    if (initialValue && open) {
      try {
        const parsed = JSON.parse(initialValue);
        if (parsed.type === 'manual') {
          setTabIndex(0);
          setLineOne(parsed.lineOne || '');
          setLineTwo(parsed.lineTwo || '');
          setSuburb(parsed.suburb || '');
          setState(parsed.state || '');
          setPostcode(parsed.postcode || '');
          setCountry(parsed.country || '');
        } else if (parsed.type === 'places') {
          setTabIndex(1);
          setSelectedPlace({
            description: parsed.description || '',
            placesId: parsed.placesId || null,
            formatted: parsed.formatted || '',
            latitude: parsed.lat || 0,
            longitude: parsed.lng || 0,
          });
        }
      } catch (e) {
        // Invalid JSON, start fresh
      }
    }
  }, [initialValue, open]);

  const handleSave = () => {
    let addressJson: string;

    if (tabIndex === 0) {
      // Manual address
      addressJson = JSON.stringify({
        type: 'manual',
        lineOne,
        lineTwo: lineTwo || null,
        suburb,
        state,
        postcode,
        country,
      });
    } else {
      // Places address
      if (!selectedPlace) {
        return;
      }
      addressJson = JSON.stringify({
        type: 'places',
        description: selectedPlace.description,
        formatted: selectedPlace.formatted,
        placesId: selectedPlace.placesId,
        lat: selectedPlace.latitude,
        lng: selectedPlace.longitude,
      });
    }

    onSave(addressJson);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Enter Address</DialogTitle>
      <DialogContent>
        <Tabs value={tabIndex} onChange={(_unusedEvent, newValue) => setTabIndex(newValue)}>
          <Tab label='Manual Entry' />
          <Tab label='Google Places' />
        </Tabs>

        <TabPanel value={tabIndex} index={0}>
          <Box className={classes.tabPanel}>
            <TextField
              className={classes.field}
              fullWidth
              label='Address Line 1'
              value={lineOne}
              onChange={(e) => setLineOne(e.target.value)}
              required
            />
            <TextField
              className={classes.field}
              fullWidth
              label='Address Line 2'
              value={lineTwo}
              onChange={(e) => setLineTwo(e.target.value)}
            />
            <TextField
              className={classes.field}
              fullWidth
              label='Suburb/City'
              value={suburb}
              onChange={(e) => setSuburb(e.target.value)}
              required
            />
            <TextField
              className={classes.field}
              fullWidth
              label='State/Province'
              value={state}
              onChange={(e) => setState(e.target.value)}
              required
            />
            <TextField
              className={classes.field}
              fullWidth
              label='Postcode'
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              required
            />
            <TextField
              className={classes.field}
              fullWidth
              label='Country'
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              required
            />
          </Box>
        </TabPanel>

        <TabPanel value={tabIndex} index={1}>
          <Box className={classes.tabPanel}>
            <AddressAutoComplete
              id='address-search'
              value={selectedPlace}
              onChange={(address) => setSelectedPlace(address)}
              label='Search for address'
            />
            {selectedPlace && (
              <Box mt={2}>
                <Typography variant='body2'>Selected: {selectedPlace.formatted}</Typography>
              </Box>
            )}
          </Box>
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          color='primary'
          variant='contained'
          disabled={tabIndex === 0 ? !lineOne || !suburb || !state || !postcode || !country : !selectedPlace}
        >
          Save Address
        </Button>
      </DialogActions>
    </Dialog>
  );
}
